import * as CoveragesUtils from './coverages.utils'
import * as _ from 'lodash';

export function isDiscountsValid(billingItemData: any, isTaxExclusive?: boolean): boolean {
  let amount: number = billingItemData.price;
  if (billingItemData.quantity) {
    amount = amount * billingItemData.quantity;
  }

  if (billingItemData.discount) {
    let discount = billingItemData.discount / 100;
    let res = (amount < 0 ? 0 : amount * discount);
    if (res > amount)
      return false;
  } else if (billingItemData.discounts) {
    let totalDiscount = 0;
    for (let discount of billingItemData.discounts) {
      if (discount.amount && discount.amount > 0) {
        totalDiscount = totalDiscount + discount.amount;
      } else if (discount.percentage && discount.percentage > 0) {
        totalDiscount = totalDiscount + ((discount.percentage / 100) * amount);
      }
    }
    if (totalDiscount > amount)
      return false;
  }
  
  return true;
}


/**
 * This will compute the total price per billing item. 
 * Total price will be computed by multiplying billing item price to the specified quantity
 * If there is an override coverage, the total price will be the override amount. (not applicable if has discount(s))
 * If there are discounts, total price will deduct the total discounts. (not applicable if has coverage(s))
 * 
 * @param  billingItemData - BillingInoviceItem data
 * @param  isTaxExclusive - If true and if has specified tax, add tax exclusive computation.
 */
export function computeBillItemTotalPrice(billingItemData: any, isTaxExclusive?: boolean): number {
  let amount: number = billingItemData.price;
  if (billingItemData.quantity) {
    amount = amount * billingItemData.quantity;
  }

  if (billingItemData.coverages && billingItemData.coverages.length > 0) {
    let index = billingItemData.coverages.findIndex((val: any) => val.type === 'override');
    if (index > -1 && billingItemData.coverages[index].amount) {
      amount = billingItemData.coverages[index].amount;
    }
  } else if (billingItemData.discount) {
    let discount = billingItemData.discount / 100;
    let res = (amount < 0 ? 0 : amount * discount);
    amount = amount - res;
  } else if (billingItemData.discounts) {
    let totalDiscount = 0;
    for (let discount of billingItemData.discounts) {
      if (discount.amount && discount.amount > 0) {
        totalDiscount = totalDiscount + discount.amount;
      } else if (discount.percentage && discount.percentage > 0) {
        totalDiscount = totalDiscount + ((discount.percentage / 100) * amount);
      }
    }
    amount = amount - totalDiscount;
  }

  if (isTaxExclusive && billingItemData && billingItemData.tax) {
    let tax = billingItemData.tax / 100;
    let res = (amount < 0 ? 0 : +amount * tax);
    amount = +amount + res;
  }

  return amount;
}

/**
 * This will compute the total amount to be paid by the payor per billing item.  
 * Total amount will be computed by multiplying billing item price to the specified quantity
 * If there is an override coverage, the total price will 0. (not applicable if has discount(s))
 * If there are deduction coverages, total price will deduct the total deductions. (not applicable if has discount(s))
 * If there are discounts, total price will deduct the total discounts. (not applicable if has coverage(s))
 * 
 * @param  billingItemData - BillingInoviceItem data
 * @param  isTaxExclusive - If true and if has specified tax, add tax exclusive computation.
 */
export function computeBillItemResolvedPrice(billingItemData: any, isTaxExclusive?: boolean): number {
  let amount: number = billingItemData.price
  if (billingItemData.quantity) {
    amount = amount * billingItemData.quantity;
  }

  if (billingItemData.coverages && billingItemData.coverages.length > 0) {
    let index = billingItemData.coverages.findIndex((val: any) => val.type === 'override');
    if (index > -1 && billingItemData.coverages[index].amount) {
      amount = 0;
    } else {
      amount = +amount - +getBillingItemCoverageDeductions(billingItemData)
      amount = amount < 0 ? 0 : amount;
    }
  } else if (billingItemData.discount) {
    let discount = billingItemData.discount / 100;
    let res = (amount < 0 ? 0 : amount * discount);
    amount = amount - res;
  } else if (billingItemData.discounts) {
    let totalDiscount = 0;
    for (let discount of billingItemData.discounts) {
      if (discount.amount && discount.amount > 0) {
        totalDiscount = totalDiscount + discount.amount;
      } else if (discount.percentage && discount.percentage > 0) {
        totalDiscount = totalDiscount + ((discount.percentage / 100) * amount);
      }
    }
    amount = amount - totalDiscount;
  }

  if (isTaxExclusive && billingItemData && billingItemData.tax) {
    let tax = billingItemData.tax / 100;
    let res = (amount < 0 ? 0 : +amount * tax);
    amount = +amount + res;
  }

  return amount;
}

/**
 * This will compute the total amount paid by the payor (PRIVATE) per billing item.  
 * billingItemData._payments are populated from BillingPayment (type: private)
 * @param  billingItemData - BillingInoviceItem data
 */
export function computeBillItemPaidPrice(billingItemData: any): number {
  let amount: number = 0;
  if (billingItemData && billingItemData._payments && billingItemData._payments.length > 0) {
    for (let payment of billingItemData._payments) {
      if (payment.type === 'private') {
        amount = amount + payment.amount;
      }
    }
  }
  return amount;
}

/**
 * This will compute the total balance amount (PRIVATE) per billing item. 
 * @param  billingItemData - BillingInoviceItem data
 */
export function computeBillItemBalancePrice(billingItemData: any): number {
  return computeBillItemResolvedPrice(billingItemData) - computeBillItemPaidPrice(billingItemData);
}





/**
 * This will compute the total coverage amount per billing item. 
 * If coverages has override, total coverage amount will be the override amount
 * Else, total all coverage deduction amount
 * @param  billingItemData - BillingInoviceItem data
 */
export function getBillingItemCoveragePrice(billingItemData: any, isTaxExclusive?: boolean): number {
  return CoveragesUtils.getBillingItemCoveragePrice(billingItemData, isTaxExclusive);
}

/**
 * This will compute the total coverage amount of the specific contract's id per billing item. 
 * If coverages has override, total coverage amount will be the override amount
 * Else, total all coverage deduction amount
 * @param  billingItemData - BillingInoviceItem data
 * @param  contractId - BillingCoverage.contract 
 * @param  isTaxExclusive - If true and if has specified tax, add tax exclusive computation.
 */
export function getBillingItemCoverageByContractIdPrice(billingItemData: any, contractId: string, isTaxExclusive?: boolean): number {
  return CoveragesUtils.getBillingItemCoverageByContractIdPrice(billingItemData, contractId, isTaxExclusive);
}

/**
 * This will compute the total coverage amount of the specific contract's insurerType per billing item. 
 * If coverages has override, total coverage amount will be the override amount
 * Else, total all coverage deduction amount
 * @param  billingItemData - BillingInoviceItem data
 * @param  insurerType - BillingCoverage.insurerType 
 * @param  insurerSubtype - BillingCoverage.insurerSubtype 
 * @param  isTaxExclusive - If true and if has specified tax, add tax exclusive computation.
 */
export function getBillingItemCoverageByInsurerTypePrice(billingItemData: any, insurerType: string, insurerSubtype: string, isTaxExclusive?: boolean): number {
  return CoveragesUtils.getBillingItemCoverageByInsurerTypePrice(billingItemData, insurerType, insurerSubtype, isTaxExclusive);
}

/**
 * This will compute the total paid amount of the specific contract's id per billing item. 
 * billingItemData._payments are populated from BillingPayment (type: insurance-coverage || corporate-partner-coverage)
 * @param  billingItemData - BillingInoviceItem data
 * @param  contractId - BillingCoverage.contract 
 */
export function getBillingItemCoverageByContractIdPaidAmount(billingItemData: any, contractId: string): number {
  return CoveragesUtils.getBillingItemCoverageByContractIdPaidAmount(billingItemData, contractId);
}

/**
 * This will compute the total paid amount of the specific contract's id per billing item. 
 * billingItemData._payments are populated from BillingPayment (type: insurance-coverage || corporate-partner-coverage)
 * @param  billingItemData - BillingInoviceItem data
 * @param  insurerType - BillingCoverage.insurerType 
 * @param  insurerSubtype - BillingCoverage.insurerSubtype 
 * @param  contractId - BillingCoverage.contract 
 */
export function getBillingItemCoverageByInsurerTypePaidAmount(billingItemData: any, insurerType: string, insurerSubtype: string): number {
  return CoveragesUtils.getBillingItemCoverageByInsurerTypePaidAmount(billingItemData, insurerType, insurerSubtype);
}

/**
 * This will compute the total deduction amount of all the coverages per billing item. 
 * @param  billingItemData - BillingInoviceItem data
 * @param  isTaxExclusive - If true and if has specified tax, add tax exclusive computation.
 */
export function getBillingItemCoverageDeductions(billingItemData: any, isTaxExclusive?: boolean): number {
  return CoveragesUtils.getBillingItemCoverageDeductions(billingItemData, isTaxExclusive);
}







/**
 * This will merge all the total tax per tax code used in billing items.
 * @param  billingItems - BillingInoviceItem data
 * @param  isTaxExclusive - If true and if has specified tax, add tax exclusive computation.
 */
export function getBillingItemsTaxes(billingItems: any[], isTaxExclusive: boolean): any[] {
  let taxes: any[] = [];

  if (billingItems && billingItems.length > 0) {
    for (let billingItem of billingItems) {
      if (billingItem && billingItem.tax) {
        let amount = (Number(computeBillItemTotalPrice(billingItem)));
        let tax = billingItem.tax / 100;
        let code = billingItem.taxCode;
        let res = isTaxExclusive ? (amount * tax) : ((amount / (1 + tax)) * tax);

        let index = taxes.length > 0 ? taxes.findIndex(val => val.code === code && val.tax === billingItem.tax) : -1;
        if (index > -1) {
          taxes[index].value = taxes[index].value + res;
        } else {
          taxes.push({ code: code, value: res, tax: billingItem.tax });
        }
      }
    }
  }

  return taxes;
}


/**
 * This will populate 'totalPrice', 'resolvedPrice', 'paid', and 'balance' in a billing item
 * @param  billingItemData - BillingInoviceItem data
 */
export function populateBillingItemComputations(billingItem: any): any {
  return {
    ...billingItem,
    totalPrice: computeBillItemTotalPrice(billingItem),
    resolvedPrice: computeBillItemResolvedPrice(billingItem),
    paid: computeBillItemPaidPrice(billingItem),
    balance: computeBillItemBalancePrice(billingItem),
  };
}

/**
 * This will populate 'totalPrice', 'resolvedPrice', 'paid', and 'balance' in billing items
 * @param  billingItems - BillingInoviceItem data
 */
export function populateBillingItemsComputations(billingItems: any[]): any[] {
  return billingItems ? billingItems.map(item => ({
    ...item,
    totalPrice: computeBillItemTotalPrice(item),
    resolvedPrice: computeBillItemResolvedPrice(item),
    paid: computeBillItemPaidPrice(item),
    balance: computeBillItemBalancePrice(item),
  })) : [];
}

/**
 * This will compute total amount of the billing items
 * @param  billingItems - BillingInoviceItem data
 * @param  isTaxExclusive - If true and if has specified tax, add tax exclusive computation.
 */
export function computeTotalAmount(billingItems: any[], isTaxExclusive: boolean): number {
  let amount: number = 0;
  if (billingItems && billingItems.length > 0) {
    for (let service of billingItems) {
      if (service) {
        let newValue = computeBillItemTotalPrice(service);
        amount = amount + (newValue);
      }
    }
  }

  if (isTaxExclusive) {
    let taxes = getBillingItemsTaxes(billingItems, isTaxExclusive);
    let newTotal = amount;
    for (let tax of taxes) {
      newTotal = newTotal + tax.value;
    }
    return newTotal;
  }

  return amount;
}

/**
 * This will compute sub total amount of the billing items (excluding tax)
 * @param  billingItems - BillingInoviceItem data
 * @param  isTaxExclusive - If true and if has specified tax, add tax exclusive computation.
 */
export function computeSubTotalAmount(billingItems: any[], isTaxExclusive?: boolean): number {
  let totalAmount = computeTotalAmount(billingItems, false);

  return totalAmount;
}




/**
 * This will compute private total amount of the billing items
 * @param  billingItems - BillingInoviceItem data
 * @param  isTaxExclusive - If true and if has specified tax, add tax exclusive computation.
 */
export function computePatientTotalAmount(billingItems: any[], isTaxExclusive?: boolean): number {
  let amount: number = 0;
  if (billingItems && billingItems.length > 0) {
    for (let billingItem of billingItems) {
      if (billingItem) {
        let newValue = computeBillItemResolvedPrice(billingItem);
        amount = amount + (newValue < 0 ? 0 : +newValue);
        if (isTaxExclusive && billingItem.tax) {
          let tax = billingItem.tax / 100;
          let code = billingItem.taxCode;
          let res = ((newValue < 0 ? 0 : +newValue) * tax);
          amount = amount + res;
        }
      }
    }
  }

  return amount;
}

/**
 * This will compute private total paid amount of the billing items
 * @param  billingItems - BillingInoviceItem data
 */
export function computePatientTotalPaidAmount(billingItems: any[]): number {
  let amount: number = 0;
  if (billingItems && billingItems.length > 0) {
    for (let billingItem of billingItems) {
      if (billingItem && billingItem._payments && billingItem._payments.length > 0) {
        for (let payment of billingItem._payments) {
          if (payment.type === 'private') {
            amount = amount + payment.amount;
          }
        }
      }
    }
  }

  return amount;
}

/**
 * This will compute private total balance amount of the billing items
 * @param  billingItems - BillingInoviceItem data
 */
export function computePatientTotalBalanceAmount(billingItems: any[], isTaxExclusive?: boolean): number {
  let totalAmount = computePatientTotalAmount(billingItems, isTaxExclusive)
  let totalPaidAmount = computePatientTotalPaidAmount(billingItems)
  return totalAmount - totalPaidAmount;
}





/**
 * This will compute coverages total amount of the billing items
 * @param  billingItems - BillingInoviceItem data
 */
export function computeCoveragesTotalAmount(billingItems: any[], isTaxExclusive?: boolean): number {
  return CoveragesUtils.computeCoveragesTotalAmount(billingItems, isTaxExclusive);
}

/**
 * This will compute insurance coverage total paid amount of the billing items
 * @param  billingItems - BillingInoviceItem data
 */
export function computeCoveragesTotalPaidAmount(billingItems: any[], isTaxExclusive?: boolean): number {
  return CoveragesUtils.computeCoveragesTotalPaidAmount(billingItems, isTaxExclusive);
}

/**
 * This will compute insurance coverage total balance amount of the billing items
 * @param  billingItems - BillingInoviceItem data
 */
export function computeCoveragesTotalBalanceAmount(billingItems: any[], isTaxExclusive?: boolean): number {
  return CoveragesUtils.computeCoveragesTotalBalanceAmount(billingItems, isTaxExclusive);
}

/**
 * This will compute insurance coverage total amount of the billing items by contract id
 * @param  billingItems - BillingInoviceItem data
 */
export function computeContractIdTotalAmount(billingItems: any[], contractId: string, isTaxExclusive?: boolean): number {
  return CoveragesUtils.computeContractIdTotalAmount(billingItems, contractId, isTaxExclusive);
}

/**
 * This will compute insurance coverage total paid amount of the billing items by contract id
 * @param  billingItems - BillingInoviceItem data
 */
export function computeContractIdTotalPaidAmount(billingItems: any[], contractId: string): number {
  return CoveragesUtils.computeContractIdTotalPaidAmount(billingItems, contractId);
}

/**
 * This will compute insurance coverage total balance amount of the billing items by contract id
 * @param  billingItems - BillingInoviceItem data
 */
export function computeContractIdTotalBalanceAmount(billingItems: any[], contractId: string, isTaxExclusive?: boolean): number {
  return CoveragesUtils.computeContractIdTotalBalanceAmount(billingItems, contractId, isTaxExclusive);
}












// 
// TRANSACTIONS
// 

export function populateTransactions(billingItems: any[], isPrivateViewDisabled: boolean, isCoverageViewDisabled: boolean, isTaxExclusive?: boolean): any[] {
  let transactions: any[] = [];
  if (billingItems && billingItems.length > 0) {
    transactions = [];
    if (!isPrivateViewDisabled) {
      transactions.push(populatePatientTransaction(billingItems, isTaxExclusive))
    }

    if (!isCoverageViewDisabled) {
      transactions = transactions.concat(populateCoveragesTransactions(billingItems, isTaxExclusive));
    }
  } else {
    transactions = undefined
  }

  return transactions;
}

export function populatePatientTransaction(billingItems: any[], isTaxExclusive?: boolean): any {
  let patientEntity: any = {
    insurer: undefined,
    insurerType: undefined,
    insurerSubtype: undefined,
    name: 'Patient',
    billingItems: []
  };

  if (billingItems && billingItems.length > 0) {
    for (let i = 0; i < billingItems.length; i++) {
      let billingItem = billingItems[i];
      if ((!billingItem.coverages || billingItem.coverages.length < 1 ||
        (billingItem.coverages && billingItem.coverages.findIndex((val: any) => val.type === 'override') === -1)) &&
        computeBillItemResolvedPrice(billingItem, isTaxExclusive) > 0) {

        let amountPaid = 0;
        let payments = billingItem._payments ? billingItem._payments.filter((payment: any) => payment.type === 'private') : [];
        payments.map((payment: any) => {
          amountPaid = amountPaid + payment.amount;
          return payment
        });

        patientEntity.billingItems.push({
          id: billingItems[i].id,
          index: i,
          coverageId: undefined,
          coverageIndex: undefined,
          billingItem: billingItem,
          charge: computeBillItemResolvedPrice(billingItem, isTaxExclusive),
          amount: amountPaid,
          payments: payments,
        });
      }
    }
  }

  return patientEntity;
}

export function populateCoveragesTransactions(billingItems: any[], isTaxExclusive?: boolean): any[] {
  let transactions: any[] = [];
  if (billingItems && billingItems.length > 0) {
    for (let i = 0; i < billingItems.length; i++) {
      let billingItem = billingItems[i];
      if (billingItem.coverages && billingItem.coverages.length > 0) {
        for (let j = 0; j < billingItem.coverages.length; j++) {
          let coverage = billingItem.coverages[j];
          let index = transactions.findIndex(val => val.insurer === coverage.insurer)
          if (getBillingItemCoveragePrice(billingItems[i], isTaxExclusive) > 0) {
            let charge = coverage.amount ? +coverage.amount :
              (coverage.percentage ? ((+coverage.percentage / 100) * +billingItems[i].price) : 0);
            if (isTaxExclusive && billingItems[i].tax) {
              let tax = billingItems[i].tax / 100;
              let res = (charge < 0 ? 0 : +charge * tax);
              charge = +charge + res;
            }

            let amountPaid = 0;
            let payments = billingItems[i]._payments ?
              billingItems[i]._payments.filter((val: any) => (val.coverage && coverage.id === val.coverage)) : []

            payments.map((payment: any) => {
              amountPaid = amountPaid + payment.amount;
              return payment
            });

            if (index === -1) {
              transactions.push({
                insurer: coverage.insurer,
                insurerType: coverage.insurerType,
                insurerSubtype: coverage.insurerSubtype,
                name: coverage._insurerName,
                billingItems: [{
                  id: billingItems[i].id,
                  coverageId: coverage.id,
                  coverageIndex: j,
                  index: i,
                  billingItem,
                  charge: charge,
                  amount: amountPaid,
                  payments: payments
                }]
              })
            } else {
              transactions[index].billingItems.push({
                id: billingItems[i].id,
                coverageId: coverage.id,
                coverageIndex: j,
                index: i,
                billingItem,
                charge: charge,
                amount: amountPaid,
                payments: payments
              })
            }
          }
        }
      }
    }
  }

  return transactions;
}




//
// COMMISSIONS
//
export function computeBillItemDoctorCommissions(billingItemData: any, isTaxExclusive?: boolean): number {
  let amount: number = 0;
  let privateAmount: number = computeBillItemResolvedPrice(billingItemData, isTaxExclusive);
  let hmoAmount: number = getBillingItemCoverageByInsurerTypePrice(billingItemData, 'insurance', 'hmo', isTaxExclusive);
  let governmentAmount: number = getBillingItemCoverageByInsurerTypePrice(billingItemData, 'insurance', 'government', isTaxExclusive);

  if (billingItemData && billingItemData.commissions) {
    for (let commission of billingItemData.commissions) {
      if (commission.type === 'private') {
        amount = amount + ((commission.percentage / 100) * (privateAmount));
      } else if (commission.type === 'insured') {
        if (commission.subtype === 'hmo') {
          amount = amount + ((commission.percentage / 100) * (hmoAmount));
        } else if (commission.subtype === 'government') {
          amount = amount + ((commission.percentage / 100) * (governmentAmount));
        }
      }
    }
  }

  return amount;
}

export function populateDoctorCommissions(billingItems: any[], isTaxExclusive?: boolean): any[] {
  let commissions: any[] = [];
  if (billingItems && billingItems.length > 0) {
    for (let i = 0; i < billingItems.length; i++) {
      let billingItem = billingItems[i];
      let privateTotal = computeBillItemResolvedPrice(billingItem, isTaxExclusive);
      let hmoTotal = getBillingItemCoverageByInsurerTypePrice(billingItem, 'insurance', 'hmo', isTaxExclusive);
      let governmentTotal = getBillingItemCoverageByInsurerTypePrice(billingItem, 'insurance', 'government', isTaxExclusive);
      let newCommissions: any[] = [];
      if (billingItem.commissions && billingItem.commissions.length > 0) {
        for (let j = 0; j < billingItem.commissions.length; j++) {
          let commission = billingItem.commissions[j];
          let index = newCommissions.findIndex(val => val.provider === commission.provider);
          if (index > -1) {
            if (commission.type === 'private') {
              newCommissions[index].privateId = commission.id;
              newCommissions[index].privatePct = commission.percentage;
            } else if (commission.type === 'insured') {
              if (commission.subtype === 'hmo') {
                newCommissions[index].hmoId = commission.id;
                newCommissions[index].hmoPct = commission.percentage;
              } else if (commission.subtype === 'government') {
                newCommissions[index].governmentId = commission.id;
                newCommissions[index].governmentPct = commission.percentage;
              }
            }
          } else {
            newCommissions.push({
              provider: commission.provider,
              privateId: commission.type === 'private' ? commission.id : undefined,
              privatePct: commission.type === 'private' ? commission.percentage : undefined,
              hmoId: commission.type === 'insured' && commission.subtype === 'hmo' ? commission.id : undefined,
              hmoPct: commission.type === 'insured' && commission.subtype === 'hmo' ? commission.percentage : undefined,
              governmentId: commission.type === 'insured' && commission.subtype === 'government' ? commission.id : undefined,
              governmentPct: commission.type === 'insured' && commission.subtype === 'government' ? commission.percentage : undefined,
            });
          }
        }
      }

      commissions.push({
        privateTotal,
        hmoTotal,
        governmentTotal,
        commissions: newCommissions,
        billingItem,
        billingItemIndex: i
      });
    }
  }

  return commissions;
}